import { useEffect, useMemo } from "react";
import { FormikProvider } from "formik";
import { useActionChoices } from "stores";
import { ACTION_VIEW_MODE } from "constants/action-form";
import { ACTION_STATUS } from "constants/actions";
import {
  useActionModalGetContext,
  useActionModalSetContext,
} from "context/ActionModalProvider";
import ActionCause from "../cause/ActionCause";
import ActionBaseInfo from "../base-info/ActionBaseInfo";
import { useActionFormikSetup } from "../utils/useFormikSetup";
import ActionModalHeader from "../header/action-header/ActionModalHeader";
import ActionDetails from "../details/ActionDetails";
import ActionCommentsSection from "../comments/ActionCommentsSection";
import { ActionReadyFormStyled } from "../common-styled/ActionReadyFormStyled";
import ActionMediaSection from "../media-library/ActionMediaSection";
import { styled } from "@mui/material";
import clsx from "clsx";

const operationsWithAnalysisTable = ["soilSampling", "leafAnalysis"];

const ContentColumnWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  "&.attachemnts-first": {
    flexDirection: "column-reverse",
  },
}));

const ActionReadyContent = ({ selectedFields, onClose }) => {
  const { actions, isSpecificOperation } = useActionChoices();
  const { action, isBulk } = useActionModalGetContext();
  const { setAction } = useActionModalSetContext();

  const { formik, isLoading: isCallLoading } = useActionFormikSetup({
    selectedFields,
    onClose,
  });

  useEffect(() => {
    if (typeof setAction === "function") {
      setAction((prev) => ({
        ...prev,
        status: formik.values?.status,
      }));
    }
  }, [formik.values?.status, setAction]);

  const shouldShowActionDetails = useMemo(() => {
    const otherActionsTaskId = actions.other?.taskId;

    return (
      formik.values?.taskId &&
      (formik.values.taskId !== otherActionsTaskId ||
        (formik.values.taskId === otherActionsTaskId &&
          formik.values.otherAction?.operationTypeId))
    );
  }, [
    actions.other?.taskId,
    formik.values.taskId,
    formik.values.otherAction?.operationTypeId,
  ]);

  const shouldShowComments = action.type !== ACTION_VIEW_MODE.new;

  const shouldShowMedia = useMemo(() => {
    const diagnoseActionsTaskId = actions.diagnose?.taskId;
    const observationTaskId = actions.observation?.taskId;
    const actionsThatAllowMedia = [diagnoseActionsTaskId, observationTaskId];

    return actionsThatAllowMedia.includes(formik.values?.taskId);
  }, [
    actions.diagnose?.taskId,
    actions.observation?.taskId,
    formik.values?.taskId,
  ]);

  const shouldAllowMedia = useMemo(() => {
    const isPerformed =
      action.status === ACTION_STATUS.performed ||
      action.status === ACTION_STATUS.failed;
    return isPerformed;
  }, [action.status]);

  const shouldShowMediaAboveTable = useMemo(() => {
    const operationTypeId = formik?.values?.diagnoseAction?.operationTypeId;

    return operationsWithAnalysisTable.some((operation) =>
      isSpecificOperation(operation, operationTypeId),
    );
  }, [formik?.values?.diagnoseAction?.operationTypeId, isSpecificOperation]);

  return (
    <FormikProvider value={formik}>
      <ActionReadyFormStyled onSubmit={formik.handleSubmit}>
        <ActionModalHeader
          fieldId={!isBulk ? selectedFields[0].id : undefined}
          onClose={onClose}
          isLoading={isCallLoading}
        />
        <div className="action-modal-form-inner">
          <div className="action-modal-card">
            <div className="action-modal-top-box">
              <ActionCause
                viewMode={action.type}
                actionStatus={action.status}
                isRequired
              />
            </div>
            <div className="action-modal-top-box">
              <ActionBaseInfo
                viewMode={action.type}
                actionStatus={action.status}
                selectedField={!isBulk ? selectedFields[0] : undefined}
              />
            </div>
          </div>
          <ContentColumnWrapper
            className={clsx({
              "attachemnts-first": shouldShowMediaAboveTable,
            })}
          >
            {shouldShowActionDetails && (
              <div className="action-details-wrapper">
                <ActionDetails
                  viewMode={action.type}
                  actionStatus={action.status}
                />
              </div>
            )}
            {shouldShowMedia && (
              <div className="action-modal-card">
                <ActionMediaSection isAllowed={shouldAllowMedia} />
              </div>
            )}
          </ContentColumnWrapper>
          {shouldShowComments && (
            <div className="action-modal-card">
              <ActionCommentsSection />
            </div>
          )}
        </div>
      </ActionReadyFormStyled>
    </FormikProvider>
  );
};

export default ActionReadyContent;
